
import React, { useEffect, useState } from "react";
import { apiClient } from "../services/ApiServices";
import {
  Calendar,
  Check,
  CheckCircle,
  Loader2,
  Locate,
  Mail,
  Phone,
  Repeat,
  User,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

function FaydaCallback() {
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [data, setData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchFaydaData = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (!code || !state) {
        setError("Missing authentication parameters");
        setStatus("error");
        setShowPopup(true);
        return;
      }

      try {
        const response = await apiClient.post(
          "/customer/verification/fayda/callback",
          {
            code: code,
            state: state,
            redirect_uri: "https://nid.gohbetbank.com/callback/verifayda",
          }
        );
        setStatus("success");
        setData(response.data?.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load verification data");
        setStatus("error");
        setShowPopup(true);
      }
    };

    fetchFaydaData();
  }, [location.search]);

  const handleConfirmFinish = async () => {
    if (!data?.customerId) {
      setError("Missing customer data. Please try again.");
      setShowPopup(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/customer/verification/submit-for-approval', {
        customerId: data.customerId,
      });
      setShowPopup(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit for approval");
      setStatus("error");
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!data?.customerId) {
      navigate("/");
      return;
    }

    setIsCancelling(true);
    try {
      await apiClient.post('/customer/verification/cancel', {
        customerId: data.customerId,
      });
    } catch (err) {
      console.error('Cancellation failed:', err);
      // Still navigate home even if cancellation fails
    } finally {
      setIsCancelling(false);
      navigate("/");
    }
  };

  // Loading state for initial data fetch
  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full mx-4">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Loading Verification Data</h2>
          <p className="text-gray-600">Please wait while we fetch your information from Fayda...</p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse delay-75"></div>
            <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse delay-150"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:px-6 sm:py-12">
      {status === "success" && (
        <div className="w-full max-w-md sm:max-w-2xl">
          {/* Header Card */}
          <div className="bg-gradient-to-r from-[#472f92] to-[#6a4bc5] shadow-xl rounded-2xl p-5 sm:p-8 text-white mb-6 transition-all">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center mb-3 sm:mb-4">
                <User className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold mb-2 tracking-tight">
                Verify Fayda Customer Details
              </h2>

              <div className="h-1 w-20 sm:w-24 bg-gradient-to-r from-[#e4aa24] to-[#f2c94c] rounded-full mb-3 sm:mb-4"></div>

              <p className="text-purple-100 text-sm sm:text-lg max-w-sm">
                Please confirm the following details before proceeding with verification
              </p>

              {/* Decorative dots */}
              <div className="flex mt-4 sm:mt-6 space-x-2">
                <div className="w-2.5 h-2.5 rounded-full bg-white/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#e4aa24]" />
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100 transition-all">
            <div className="p-6 sm:p-10">
              {/* Section Header */}
              <div className="mb-4 flex items-center">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-purple-50 flex items-center justify-center mr-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-gradient-to-r from-[#472f92] to-[#6a4bc5] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  Customer NID Information
                </h3>
              </div>

              <p className="text-gray-500 text-sm sm:text-base mb-6">
                Review and confirm these details
              </p>

              {/* Info Cards */}
              <div className="space-y-6 mb-10">
                {/* Profile Image */}
                <div className="flex justify-center">
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-gray-50 to-white shadow-sm">
                    {data?.faydaData?.picture ? (
                      <img
                        src={data.faydaData.picture}
                        alt="Customer Profile"
                        className="w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] object-cover rounded-xl border border-gray-200"
                      />
                    ) : (
                      <div className="w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] rounded-xl border border-gray-200 bg-gray-100 flex items-center justify-center">
                        <User className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="max-w-4xl mx-auto">
                  <div
                    className="border border-gray-200 rounded-2xl p-5 sm:p-6
               bg-gradient-to-br from-gray-50 to-white
               shadow-md"
                  >
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-5">
                      Personal Information
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      {[
                        { label: "Full Name", value: data?.faydaData?.name, icon: User },
                        { label: "Birth Date", value: data?.faydaData?.birthdate, icon: Calendar },
                        { label: "Gender", value: data?.faydaData?.gender, icon: User },
                        { label: "Phone Number", value: data?.faydaData?.phoneNumber, icon: Phone },
                        { label: "Email", value: data?.faydaData?.email, icon: Mail },
                        {label:"Nationality", value: data?.faydaData?.nationality , icon: User},
                        { label: "Region", value: data?.faydaData?.address?.region, icon: Locate },
                        { label: "Zone", value: data?.faydaData?.address?.zone, icon: Locate },
                        { label: "Woreda", value: data?.faydaData?.address?.woreda, icon: Locate },

                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-blue-600" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-500 font-medium block mb-1">
                              {item.label}:
                            </span>
                            <span className="text-base sm:text-lg font-semibold text-gray-800 break-words">
                              {item.value || "Not provided"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-5 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={handleConfirmFinish}
                  disabled={isSubmitting}
                  className={`w-full bg-gradient-to-r from-[#472f92] via-[#5a3cad] to-[#e4aa24]
                    text-white py-3.5 sm:py-4 rounded-xl font-semibold text-base sm:text-lg
                    shadow-md transition-all flex items-center justify-center gap-3
                    ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-95 active:scale-[0.98]'}`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Confirm and Finish</span>
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </>
                  )}
                </button>

                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className={`w-full bg-white border border-gray-300
                    text-gray-700 py-3.5 sm:py-4 rounded-xl font-medium text-base sm:text-lg
                    transition-all flex items-center justify-center gap-3
                    ${isCancelling ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-50 active:scale-[0.98]'}`}
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Cancelling...</span>
                    </>
                  ) : (
                    <>
                      <span>Cancel</span>
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <X className="w-4 h-4 text-gray-600" />
                      </div>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error State without popup */}
      {status === "error" && !showPopup && (
        <div className="text-center max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white py-3 rounded-xl font-bold hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scaleIn">
            <div className="flex items-center gap-3 mb-4">
              {status === "success" ? (
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-red-600" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {status === "success" ? "Success!" : "Oops!"}
                </h3>
                <p className="text-sm text-gray-500">
                  {status === "success" ? "Verification completed" : "Something went wrong"}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                {status === "success"
                  ? "Your verification data has been successfully submitted for approval. You will be notified once the process is complete."
                  : error || "An unexpected error occurred. Please try again."}
              </p>
            </div>

            <div className="space-y-3">
              {status === "success" ? (
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-gradient-to-r from-[#472f92] to-[#e4aa24] 
                    text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2
                    hover:opacity-90 transition-opacity"
                >
                  <Check className="w-5 h-5" />
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-gradient-to-r from-[#472f92] to-[#e4aa24] 
                      text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2
                      hover:opacity-90 transition-opacity"
                  >
                    <Repeat className="w-5 h-5" />
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate("/")}
                    className="w-full bg-gray-100 text-gray-700 py-3.5 rounded-xl 
                      font-medium hover:bg-gray-200 transition-colors"
                  >
                    Go to Homepage
                  </button>
                </>
              )}
            </div>

            {status === "success" && (
              <p className="text-center text-sm text-gray-500 mt-4">
                You can close this window
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Add CSS animation for popup
const styles = `
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.animate-scaleIn {
  animation: scaleIn 0.2s ease-out;
}
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default FaydaCallback;